<template>
  <div class="withdraw-approval-container">
    <div class="page-header">
      <h2>提现审批管理</h2>
      <p class="subtitle">审核用户提现申请，审核通过后点击发放完成转账</p>
    </div>

    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stat-cards">
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon pending"><el-icon><Clock /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.pending }}</div>
            <div class="stat-label">待审批</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon approved"><el-icon><Select /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">{{ stats.approved }}</div>
            <div class="stat-label">待发放</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon amount"><el-icon><Money /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">¥{{ stats.todayAmount || 0 }}</div>
            <div class="stat-label">今日提现总额</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-icon month"><el-icon><TrendCharts /></el-icon></div>
          <div class="stat-content">
            <div class="stat-value">¥{{ stats.monthAmount || 0 }}</div>
            <div class="stat-label">本月提现总额</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 搜索筛选 -->
    <el-card shadow="never" class="filter-card">
      <el-form :inline="true" :model="queryParams">
        <el-form-item label="状态">
          <el-select v-model="queryParams.status" placeholder="全部" clearable style="width: 120px">
            <el-option label="待审批" value="pending" />
            <el-option label="待发放" value="approved" />
            <el-option label="已发放" value="paid" />
            <el-option label="已驳回" value="rejected" />
          </el-select>
        </el-form-item>
        <el-form-item label="用户角色">
          <el-select v-model="queryParams.userRole" placeholder="全部" clearable style="width: 120px">
            <el-option label="债权人" value="creditor" />
          </el-select>
        </el-form-item>
        <el-form-item label="日期范围">
          <el-date-picker v-model="queryParams.dateRange" type="daterange" start-placeholder="开始日期" end-placeholder="结束日期" style="width: 240px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="loadList">搜索</el-button>
          <el-button @click="resetQuery">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 数据表格 -->
    <el-card shadow="never" class="table-card">
      <el-table :data="tableData" v-loading="loading" stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="申请人" width="180">
          <template #default="{ row }">
            <div>
              <div class="user-name">{{ row.user?.realName || "-" }}</div>
              <div class="user-mobile">{{ row.user?.mobile || "-" }}</div>
              <el-tag size="small" type="primary">债权人</el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="申请金额" width="120">
          <template #default="{ row }">¥{{ row.amount }}</template>
        </el-table-column>
        <el-table-column prop="fee" label="手续费(10%)" width="120">
          <template #default="{ row }">¥{{ row.fee || (row.amount * 0.1).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column prop="actualAmount" label="实际到账" width="120">
          <template #default="{ row }">¥{{ row.actualAmount || (row.amount * 0.9).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="收款账户" min-width="200">
          <template #default="{ row }">
            <div v-if="row.withdrawType === 'wechat'" class="account-info">
              <el-icon><Wallet /></el-icon> 微信零钱
            </div>
            <div v-else-if="row.bankInfo" class="account-info">
              <el-icon><CreditCard /></el-icon> {{ row.bankInfo.bankName }} 尾号{{ row.bankInfo.lastFour }}
            </div>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="申请时间" width="170">
          <template #default="{ row }">{{ formatDate(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="statusMap[row.status]?.type">{{ statusMap[row.status]?.label }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template #default="{ row }">
            <template v-if="row.status === 'pending'">
              <el-button type="success" size="small" @click="handleApprove(row)">通过</el-button>
              <el-button type="danger" size="small" @click="handleReject(row)">驳回</el-button>
            </template>
            <template v-else-if="row.status === 'approved'">
              <el-button type="primary" size="small" @click="handlePay(row)">发放</el-button>
              <el-button type="danger" size="small" plain @click="handleReject(row)">驳回</el-button>
            </template>
            <el-button type="primary" size="small" link @click="showDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrapper">
        <el-pagination v-model:current-page="queryParams.page" v-model:page-size="queryParams.pageSize" :total="total" :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next" @change="loadList" />
      </div>
    </el-card>

    <!-- 详情弹窗 -->
    <el-dialog v-model="detailVisible" title="提现详情" width="600px">
      <el-descriptions :column="2" border v-if="currentItem">
        <el-descriptions-item label="ID">{{ currentItem.id }}</el-descriptions-item>
        <el-descriptions-item label="状态"><el-tag :type="statusMap[currentItem.status]?.type">{{ statusMap[currentItem.status]?.label }}</el-tag></el-descriptions-item>
        <el-descriptions-item label="申请人">{{ currentItem.user?.realName }}</el-descriptions-item>
        <el-descriptions-item label="手机号">{{ currentItem.user?.mobile }}</el-descriptions-item>
        <el-descriptions-item label="申请金额">¥{{ currentItem.amount }}</el-descriptions-item>
        <el-descriptions-item label="手续费">¥{{ currentItem.fee || (currentItem.amount * 0.1).toFixed(2) }}</el-descriptions-item>
        <el-descriptions-item label="实际到账">¥{{ currentItem.actualAmount || (currentItem.amount * 0.9).toFixed(2) }}</el-descriptions-item>
        <el-descriptions-item label="申请时间">{{ formatDate(currentItem.createdAt) }}</el-descriptions-item>
        <el-descriptions-item label="收款方式">
          {{ currentItem.withdrawType === 'wechat' ? '微信零钱' : '银行卡' }}
        </el-descriptions-item>
        <el-descriptions-item label="银行卡" v-if="currentItem.bankInfo">{{ currentItem.bankInfo?.bankName }} 尾号{{ currentItem.bankInfo?.lastFour }}</el-descriptions-item>
        <el-descriptions-item label="驳回原因" :span="2" v-if="currentItem.rejectionReason">{{ currentItem.rejectionReason }}</el-descriptions-item>
        <el-descriptions-item label="发放时间" :span="2" v-if="currentItem.paidAt">{{ formatDate(currentItem.paidAt) }}</el-descriptions-item>
        <el-descriptions-item label="交易流水号" :span="2" v-if="currentItem.transactionId">{{ currentItem.transactionId }}</el-descriptions-item>
      </el-descriptions>
    </el-dialog>

    <!-- 驳回原因弹窗 -->
    <el-dialog v-model="rejectVisible" title="驳回原因" width="400px">
      <el-input v-model="rejectReason" type="textarea" :rows="3" placeholder="请输入驳回原因" />
      <template #footer>
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" @click="confirmReject">确认驳回</el-button>
      </template>
    </el-dialog>

    <!-- 发放确认弹窗 -->
    <el-dialog v-model="payVisible" title="确认发放" width="500px">
      <el-alert type="warning" :closable="false" style="margin-bottom: 20px;">
        <template #title>
          <strong>注意：</strong>发放后将调用支付接口转账，请确认信息无误
        </template>
      </el-alert>
      <el-descriptions :column="1" border v-if="payItem">
        <el-descriptions-item label="申请人">{{ payItem.user?.realName }} ({{ payItem.user?.mobile }})</el-descriptions-item>
        <el-descriptions-item label="申请金额">¥{{ payItem.amount }}</el-descriptions-item>
        <el-descriptions-item label="手续费">¥{{ payItem.fee || (payItem.amount * 0.1).toFixed(2) }}</el-descriptions-item>
        <el-descriptions-item label="实际到账">
          <span style="font-size: 20px; color: #16499B; font-weight: 700;">¥{{ payItem.actualAmount || (payItem.amount * 0.9).toFixed(2) }}</span>
        </el-descriptions-item>
        <el-descriptions-item label="收款方式">
          {{ payItem.withdrawType === 'wechat' ? '微信零钱' : '银行卡' }}
          <span v-if="payItem.bankInfo">：{{ payItem.bankInfo?.bankName }} 尾号{{ payItem.bankInfo?.lastFour }}</span>
        </el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="payVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmPay" :loading="payLoading">确认发放</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue"
import { ElMessage } from "element-plus"
import { Clock, Money, TrendCharts, Select, Wallet, CreditCard } from "@element-plus/icons-vue"
import adminService from "@/api/admin/index.js"

const loading = ref(false)
const tableData = ref([])
const total = ref(0)
const stats = ref({ pending: 0, approved: 0, todayAmount: 0, monthAmount: 0 })
const queryParams = ref({ page: 1, pageSize: 10, status: "", userRole: "", dateRange: null })
const detailVisible = ref(false)
const currentItem = ref(null)
const rejectVisible = ref(false)
const rejectReason = ref("")
const rejectItem = ref(null)
const payVisible = ref(false)
const payItem = ref(null)
const payLoading = ref(false)

const statusMap = {
  pending: { label: "待审批", type: "warning" },
  approved: { label: "待发放", type: "success" },
  paid: { label: "已发放", type: "info" },
  rejected: { label: "已驳回", type: "danger" }
}

const formatDate = (date) => date ? new Date(date).toLocaleString("zh-CN") : "-"

const loadList = async () => {
  loading.value = true
  try {
    const params = { ...queryParams.value }
    if (params.dateRange && params.dateRange.length === 2) {
      params.startDate = params.dateRange[0]
      params.endDate = params.dateRange[1]
      delete params.dateRange
    }
    const res = await adminService.get("/admin/credits/withdraw/list", { params })
    if (res.code === 200 && res.data) {
      tableData.value = res.data.items || []
      total.value = res.data.total || 0
    }
  } catch (err) {
    tableData.value = []
    total.value = 0
  } finally {
    loading.value = false
  }
}

const loadStats = async () => {
  try {
    const res = await adminService.get("/admin/credits/withdraw/statistics")
    if (res.code === 200 && res.data) {
      stats.value = res.data
    }
  } catch (err) {
    stats.value = { pending: 0, approved: 0, todayAmount: 0, monthAmount: 0 }
  }
}

const resetQuery = () => {
  queryParams.value = { page: 1, pageSize: 10, status: "", userRole: "", dateRange: null }
  loadList()
}

// 审核通过
const handleApprove = async (row) => {
  try {
    const res = await adminService.post("/admin/credits/withdraw/approve", { id: row.id })
    if (res.code === 200) {
      ElMessage.success("审批通过，等待发放")
      loadList()
      loadStats()
    } else {
      ElMessage.error(res.message || "操作失败")
    }
  } catch (err) {
    ElMessage.error("操作失败")
  }
}

// 驳回
const handleReject = (row) => {
  rejectItem.value = row
  rejectReason.value = ""
  rejectVisible.value = true
}

const confirmReject = async () => {
  if (!rejectReason.value) {
    ElMessage.warning("请输入驳回原因")
    return
  }
  try {
    const res = await adminService.post("/admin/credits/withdraw/reject", { id: rejectItem.value.id, reason: rejectReason.value })
    if (res.code === 200) {
      ElMessage.success("已驳回")
      rejectVisible.value = false
      loadList()
      loadStats()
    } else {
      ElMessage.error(res.message || "操作失败")
    }
  } catch (err) {
    ElMessage.error("操作失败")
  }
}

// 发放
const handlePay = (row) => {
  payItem.value = row
  payVisible.value = true
}

const confirmPay = async () => {
  payLoading.value = true
  try {
    const res = await adminService.post("/admin/credits/withdraw/pay", { id: payItem.value.id })
    if (res.code === 200) {
      ElMessage.success("发放成功")
      payVisible.value = false
      loadList()
      loadStats()
    } else {
      ElMessage.error(res.message || "发放失败")
    }
  } catch (err) {
    ElMessage.error("发放失败，请检查支付配置")
  } finally {
    payLoading.value = false
  }
}

const showDetail = (row) => {
  currentItem.value = row
  detailVisible.value = true
}

onMounted(() => {
  loadList()
  loadStats()
})
</script>

<style scoped>
.withdraw-approval-container { padding: 20px; }
.page-header { margin-bottom: 20px; }
.page-header h2 { margin: 0; font-size: 24px; font-weight: 600; color: #ffffff !important; }
.subtitle { margin: 8px 0 0; color: #e2e8f0 !important; font-size: 14px; }
.stat-cards { margin-bottom: 20px; }
.stat-card { display: flex; align-items: center; padding: 20px; border-radius: 12px; }
.stat-card :deep(.el-card__body) { display: flex; align-items: center; width: 100%; padding: 0; }
.stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 24px; margin-right: 16px; }
.stat-icon.pending { background: linear-gradient(135deg, #fef3c7, #fde68a); color: #d97706; }
.stat-icon.approved { background: linear-gradient(135deg, #d1fae5, #a7f3d0); color: #0AA946; }
.stat-icon.amount { background: linear-gradient(135deg, #dbeafe, #bfdbfe); color: #16499B; }
.stat-icon.month { background: linear-gradient(135deg, #e0e7ff, #c7d2fe); color: #16499B; }
.stat-content { flex: 1; }
.stat-value { font-size: 24px; font-weight: 700; color: #1e293b; }
.stat-label { font-size: 14px; color: #64748b; margin-top: 4px; }
.filter-card { margin-bottom: 20px; border-radius: 12px; }
.table-card { border-radius: 12px; }
.pagination-wrapper { display: flex; justify-content: flex-end; margin-top: 20px; }
.user-name { font-weight: 500; color: #1e293b; }
.user-mobile { font-size: 12px; color: #666666; margin-bottom: 4px; }
.account-info { display: flex; align-items: center; gap: 6px; }
</style>
